import * as React from 'react';

import {
    Block,
    type Change,
    isBlockRangeSelection,
    type SelectionRange,
    type TextRange,
    type Value,
} from '@gitbook/slate';
import type { ErrorBoundaryProps } from '@gitbook/util-debug';
import { List } from '@gitbook/util-immutable';
import { assert } from '@gitbook/util-ts';

import { EditorContext, EditorFocusContext } from '../contexts';
import type { EditorContainer } from '../interfaces';
import { AfterPlugin, BeforePlugin, type Plugin, PluginsStack } from '../plugins';
import { getChildrenRanges, isOnlySelectedNode, noop } from '../utils';
import { updateSelection, useEventHandlers } from './event-handlers';
import NodeRenderer from './NodeRenderer';
import { NodeVirtualRenderer } from './NodeVirtualRenderer';
import { useVirtualizedNodeList } from './virtualization';

export interface EditorProps {
    value: Value;
    testID?: string;
    readOnly?: boolean;
    autoCorrect?: boolean;
    autoFocus?: boolean;
    spellCheck?: boolean;
    tabIndex?: number;
    className?: string;
    childrenClassName?: string;
    role?: string;
    style?: React.CSSProperties;
    plugins?: Plugin[];
    decorations?: TextRange[] | List<TextRange>;
    selections?: List<TextRange>;
    /**
     * Error boundary fallback when rendering a node fails.
     */
    nodeErrorFallback?: ErrorBoundaryProps['fallback'];

    /**
     * Enable rendering virtualization for the editor content.
     * When document is large, it can highly improve performances.
     */
    virtualize?: boolean;
    virtualizeChildren?: boolean;

    /**
     * Optimize rendering performances by having the browser only render
     * the layout of blocks when they enter the viewport.
     *
     * When this option is enabled, plugins cannot render any content outside the layout box of the node elements.
     */
    enableBlockContainment?: boolean;

    /**
     * Callback when the content is updated.
     */
    onChange: (change: Change) => void;

    /**
     * Content to render in the context of the editor (can access the EditorContext)
     */
    children?: React.ReactNode;
}

/*
 * Main component to render a slate editor.
 */
function EditorWithRef(props: EditorProps, ref: React.Ref<EditorContainer>) {
    const {
        value,
        readOnly,
        role,
        spellCheck,
        tabIndex,
        style,
        className,
        plugins: propPlugins,
        decorations = [],
        selections = List.create(),
        virtualize = false,
        virtualizeChildren = false,
        enableBlockContainment = false,
        onChange,
        children,
        testID,
        autoFocus,
        autoCorrect,
        nodeErrorFallback = undefined,
    } = props;
    const { document, selection, isFocused } = value;

    const isUpdatingSelectionRef = React.useRef(false);
    const domRef = React.useRef<HTMLDivElement>(null);
    const [error, setError] = React.useState<Error | null>(null);
    if (error) {
        throw error;
    }

    /*
     * When the selection is a block range we need to render a hidden span element
     * in the editor with a native range so that we can capture clipboard events (cut/copy).
     * `blockSelectionRef` is a reference to that span element. We can then set a
     * native range on the element when we updateSelection.
     */
    const blockSelectionRef = React.useRef<HTMLSpanElement>(null);
    /*
     * Create the editor instance.
     *
     * And compute the stack of plugins that should be used.
     *
     * In addition to the plugins provided in props, this will initialize three
     * other plugins:
     *
     * - The top-level editor plugin, which allows for top-level handlers, etc.
     * - The two "core" plugins, one before all the other and one after.
     */
    // biome-ignore lint/correctness/useExhaustiveDependencies: if you add `value` to the dependencies things can explode, max depth exceeded can happen.
    const editor = React.useMemo<EditorContainer>(() => {
        // Create mutable "Editor" reference
        const instance = {
            readOnly: readOnly || false,
            isComposing: false,
            value,
            elementRef: domRef,
            onChange(_change: Change) {},
            change(_fn: (change: Change) => void) {},
            isUpdatingSelection: isUpdatingSelectionRef,
            blockSelectionRef,
            stack: PluginsStack.create([BeforePlugin(), ...(propPlugins || []), AfterPlugin()]),
            valueDecorations: List.create(),
            virtualize,
            virtualizeChildren,
        };

        instance.change = (fn: (change: Change) => void) => {
            const change = instance.value.change().call(fn);
            instance.onChange(change);
        };

        return instance;
    }, [propPlugins, readOnly, virtualize, virtualizeChildren]);

    editor.readOnly = readOnly || false;
    editor.value = value;
    editor.virtualize = virtualize;
    editor.virtualizeChildren = virtualize && virtualizeChildren;

    editor.onChange = (change: Change) => {
        if (editor.readOnly) {
            return;
        }

        // All changes made when editing are going into the history.
        change.finalize();

        editor.stack.run('onChange', change, editor);
        if (change.value === editor.value) {
            return;
        }
        // Update the referenced value before it gets updated by rendering
        // since React may batch events before rendering.
        const previousValue = editor.value;
        editor.value = change.value;
        try {
            onChange(change);
        } catch (error) {
            // In case of an error in the onChange, we crash the editor
            editor.value = previousValue;
            setError(error);
            throw error;
        }
    };

    React.useImperativeHandle(ref, () => editor);

    //
    // On every render, update the selection to ensure it's well synced with the DOM
    // when this one is mutating.
    //
    const previousSelectionRef = React.useRef<SelectionRange | null>(null);

    React.useLayoutEffect(() => {
        const element = domRef.current;
        if (!element) {
            return;
        }
        updateSelection(element, editor, {
            // Only scroll to the selection when it changes
            // since with virtualization the component is re-rendering when scrolling
            // and we don't want to scroll back to where the selection is.
            scrollToSelection: previousSelectionRef.current !== editor.value.selection,
        });
        previousSelectionRef.current = editor.value.selection;
    });

    const virtualized = useVirtualizedNodeList({
        editor,
        nodeList: document.nodes.toArray(),
        parentRef: editor.elementRef,
        virtualizationEnabled: virtualize,
        containmentEnabled: enableBlockContainment,
    });

    //
    // Decorations and selections
    //
    const allDecorations = editor.stack
        .getValueDecorations(value)
        .concat(editor.stack.getDecorations(document))
        .concat(decorations || []);

    const [childrenDecorations, childrenSelections] = getChildrenRanges(
        document,
        allDecorations,
        selections
    );

    const selectionIndexes = document.getSelectionIndexes(selection);

    const ancestors = List.create([document]);

    const documentChildren = virtualized.renderNodes(document, (child, vChild, index) => {
        assert(Block.isBlock(child), 'Top-level of document should only contain blocks');
        const isSelectedBlock =
            isBlockRangeSelection(selection) && selection.hasBlock(child, document);
        const isSelected =
            !!selectionIndexes && selectionIndexes.start <= index && index < selectionIndexes.end;

        // @ts-expect-error
        const { anchorKey, focusKey } = selection;

        // In order to prevent inconsistencies with selection, we need to make sure the anchor
        // and the focus of the selection are not virtualized.
        const virtualizeThisNode =
            vChild.virtual &&
            // do not virtualize the node if it is the anchor or focus of the selection
            anchorKey !== child.key &&
            focusKey !== child.key &&
            // do not virtualize the node if it contains the anchor of the selection
            (!anchorKey || !child.hasChild(anchorKey)) &&
            // do not virtualize the node if it contains the focus of the selection
            (!focusKey || !child.hasChild(focusKey));

        // There is no point in containing a node that is not being virtualized
        const containThisNode = enableBlockContainment && vChild.virtual;

        return (
            <div
                key={child.key}
                ref={vChild.ref}
                style={
                    containThisNode || virtualizeThisNode
                        ? {
                              // @ts-ignore Chrome only
                              // Optimize to have the browser skip the rendering of elements that are not in the viewport
                              // https://developer.mozilla.org/en-US/docs/Web/CSS/content-visibility
                              contentVisibility: 'auto',
                              containIntrinsicHeight: `${vChild.virtualHeight}px`,
                          }
                        : undefined
                }
                data-virtualparent={virtualizeThisNode ? 'true' : undefined}
                className={props.childrenClassName}
            >
                {virtualizeThisNode ? (
                    <NodeVirtualRenderer
                        node={child}
                        editor={editor}
                        height={vChild.virtualHeight}
                    />
                ) : (
                    <NodeRenderer
                        block={null}
                        editor={editor}
                        decorations={childrenDecorations[index]}
                        selections={childrenSelections[index]}
                        isSelected={isSelected}
                        isSelectedBlock={isSelectedBlock}
                        isBlockSelection={isBlockRangeSelection(selection)}
                        isOnlyNodeSelected={
                            isSelected && isOnlySelectedNode(selection, child, document)
                        }
                        isFocused={isFocused && isSelected}
                        node={child}
                        parent={document}
                        ancestors={ancestors}
                        readOnly={editor.readOnly}
                        nodeErrorFallback={nodeErrorFallback}
                    />
                )}
            </div>
        );
    });

    const eventHandlers = useEventHandlers({ domRef, editor });

    // Focus on mount if auto focusing
    React.useEffect(() => {
        if (autoFocus && domRef.current) {
            domRef.current.focus();
        }
    }, [autoFocus]);

    const zeroWidthSpace = '\u200B';
    return (
        <EditorContext.Provider value={editor}>
            <EditorFocusContext.Provider value={isFocused}>
                <div
                    data-testid={testID}
                    ref={domRef}
                    // data-slate-editor should alwys be true as Grammarly has a fixed condition on it
                    data-slate-editor
                    data-document-key={document.key}
                    data-key={document.key}
                    aria-multiline={readOnly ? undefined : true}
                    contentEditable={readOnly ? undefined : true}
                    suppressContentEditableWarning
                    autoCorrect={autoCorrect ? 'on' : 'off'}
                    spellCheck={spellCheck}
                    role={readOnly ? undefined : role}
                    tabIndex={tabIndex}
                    className={className}
                    {...eventHandlers}
                    style={{
                        // Prevent the default outline styles.
                        outline: 'none',
                        // Preserve adjacent whitespace and new lines.
                        whiteSpace: 'pre-wrap',
                        // Allow words to break if they are too long.
                        wordWrap: 'break-word',
                        // Allow for passed-in styles to override anything.
                        ...style,
                    }}
                >
                    {documentChildren}
                    {isBlockRangeSelection(selection) ? (
                        <span ref={blockSelectionRef} className="slate-spacer">
                            {zeroWidthSpace}
                        </span>
                    ) : null}
                </div>
                {children}
            </EditorFocusContext.Provider>
        </EditorContext.Provider>
    );
}

export const Editor = React.forwardRef(EditorWithRef);

Editor.defaultProps = {
    autoFocus: false,
    autoCorrect: true,
    role: 'textbox',
    onChange: noop,
    plugins: [],
    readOnly: false,
    style: {},
    spellCheck: true,
};
